import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingProcessStepInput,
    DeleteLandingProcessStepInput,
    LandingProcessStep,
    ModelLandingProcessStepFilterInput,
    UpdateLandingProcessStepInput
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class LandingProcessStepService {
    private amplifyClient = generateClient();
    private _cachedSteps: Map<string, LandingProcessStep[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<LandingProcessStep[]>
    > = new Map();

    private _getCacheKey(filter: ModelLandingProcessStepFilterInput): string {
        return JSON.stringify(filter || {});
    }

    async listLandingProcessSteps(
        filter: ModelLandingProcessStepFilterInput = null
    ): Promise<LandingProcessStep[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedSteps.has(cacheKey)) {
            return this._cachedSteps.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchSteps(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchSteps(
        filter: ModelLandingProcessStepFilterInput,
        cacheKey: string
    ): Promise<LandingProcessStep[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingProcessSteps,
                    variables: { filter }
                })
            ).data.listLandingProcessSteps.items;

            if (data) {
                const steps = data as LandingProcessStep[];
                this._cachedSteps.set(cacheKey, steps);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return steps;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            console.error(
                '[LandingProcessStep] listLandingProcessSteps failed:',
                error
            );
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedSteps.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingProcessStep(id: string): Promise<LandingProcessStep> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingProcessStep,
                    variables: { id }
                })
            ).data.getLandingProcessStep;
            if (data) {
                return data as LandingProcessStep;
            }
        } catch (error) {
            console.error('Error finding LandingProcessStep:', error);
        }
        return null;
    }

    async createLandingProcessStep(
        input: CreateLandingProcessStepInput
    ): Promise<LandingProcessStep> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingProcessStep,
                    variables: { input }
                })
            ).data.createLandingProcessStep;
            if (data) {
                this.clearCache();
                return data as LandingProcessStep;
            }
        } catch (error) {
            console.error('Error creating LandingProcessStep:', error);
        }
        return null;
    }

    async updateLandingProcessStep(
        input: UpdateLandingProcessStepInput
    ): Promise<LandingProcessStep> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingProcessStep,
                    variables: { input }
                })
            ).data.updateLandingProcessStep;
            if (data) {
                this.clearCache();
                return data as LandingProcessStep;
            }
        } catch (error) {
            console.error('Error updating LandingProcessStep:', error);
        }
        return null;
    }

    async deleteLandingProcessStep(
        input: DeleteLandingProcessStepInput
    ): Promise<LandingProcessStep> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingProcessStep,
                    variables: { input }
                })
            ).data.deleteLandingProcessStep;
            if (data) {
                this.clearCache();
                return data as LandingProcessStep;
            }
        } catch (error) {
            console.error('Error deleting LandingProcessStep:', error);
        }
        return null;
    }
}
